import { EventIds } from 'constants/eventIds';
import React from 'react';
import { delay } from './delay';
import { importFn } from './importFn';
import { log } from './log';

const MaxAttempts = 5;

const exponentialBackoffRetry = async <T>(
    fn: () => Promise<T>,
    originalError: unknown,
    attempt = 1,
): Promise<T> => {
    try {
        return await fn();
    } catch (error) {
        if (attempt >= MaxAttempts) {
            throw originalError;
        }

        await delay(attempt);

        return exponentialBackoffRetry<T>(fn, originalError, ++attempt);
    }
};

const getUrlFromError = (error: unknown) => {
    const message = error instanceof Error ? error.message : '';
    const urlFromError = message.trim().split('module:').pop() ?? '';

    try {
        return new URL(urlFromError);
    } catch (e) {
        throw error;
    }
};

export const lazyWithRetries: typeof React.lazy = (importer) => {
    const retryImport = async () => {
        try {
            return await importer();
        } catch (error) {
            const url = getUrlFromError(error);

            const importUrl = async () => {
                url.searchParams.set('t', `${+new Date()}`);
                log.warn(
                    `Retry to import module`,
                    EventIds.RetryLazyImport,
                    undefined,
                    {
                        url: url.href,
                    },
                );
                return importFn(url.href);
            };

            return exponentialBackoffRetry(importUrl, error);
        }
    };

    return React.lazy(retryImport);
};
